import { PropsWithChildren, useCallback, useMemo, useRef } from 'react'

import { twMerge } from '../../../utils/twmerge'
import { Button } from '../Button'

type ButtonProps = {
  size?: 's' | 'm' | 'l'
  fill?: boolean
  full?: boolean
  gold?: boolean
  square?: boolean
  simple?: boolean
  simpleSelected?: boolean
  thin?: 'gold' | 'pink' | 'gray' | 'none'
}

type ModalProps = {
  openText?: string
  size?: 'small' | 'large'
  footer?: boolean
  footerButtonText?: string | number
  handleFooterButtonClick?: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => void
  openButtonStyles?: ButtonProps
  footerButtonStyles?: ButtonProps
  open?: boolean
  onClose?: () => void | undefined
  closable?: boolean
}

export function Modal({
  openText = '',
  size = undefined,
  footer = false,
  footerButtonText = '',
  handleFooterButtonClick = () => {},
  children = undefined,
  openButtonStyles = {},
  footerButtonStyles = {},
  open = false,
  onClose = undefined,
  closable = true,
}: PropsWithChildren<ModalProps>) {
  const ref = useRef<HTMLDialogElement>(null)

  const sizeClass = useMemo(() => {
    if (size === 'small') {
      return 'sq-modal-small'
    } else if (size === 'large') {
      return 'sq-modal-large'
    }
    return ''
  }, [size])

  const isOpenButtonStyles = useMemo(
    () => Object.keys(openButtonStyles).length !== 0,
    [openButtonStyles]
  )

  const handleModalOpen = useCallback(() => {
    if (ref.current) {
      ref.current.showModal()
    }
  }, [ref])

  const handleModalClose = useCallback(() => {
    if (ref.current) {
      ref.current.close()
      onClose?.()
    }
  }, [ref, onClose])

  return (
    <div>
      {openText && (
        <>
          {isOpenButtonStyles ? (
            <Button
              text={openText}
              onClick={handleModalOpen}
              {...openButtonStyles}
            />
          ) : (
            <button type="button" onClick={handleModalOpen}>
              {openText}
            </button>
          )}
        </>
      )}
      <dialog className={twMerge('sq-modal', sizeClass)} ref={ref} open={open}>
        <div className="sq-modal-box relative z-20 overflow-scroll">
          <form method="dialog">
            {closable && (
              <button
                type="button"
                onClick={handleModalClose}
                className="sq-modal-close sq-icon sq-icon-size-3xl"
                style={{
                  WebkitMaskImage: "url('/icons/close.svg')",
                  maskImage: "url('/icons/close.svg')",
                }}
              >
                close
              </button>
            )}
          </form>
          <div className="sq-modal-contents">{children}</div>
          {footer && (
            <div className="sq-modal-footer">
              <Button
                text={footerButtonText}
                onClick={handleFooterButtonClick}
                {...footerButtonStyles}
              />
            </div>
          )}
        </div>
        <form
          method="dialog"
          className="sq-modal-backdrop fixed inset-0 z-10 bg-gray-100 bg-opacity-70"
        >
          {closable && (
            <button type="button" onClick={handleModalClose}>
              close
            </button>
          )}
        </form>
      </dialog>
    </div>
  )
}
